import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelection } from "@/contexts/ActiveCompanyContext";
import { formatDateIT } from "@/lib/dates";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, Building2 } from "lucide-react";
import { MONTHS_IT, WEEKDAYS_FULL } from "@/lib/constants";
import { SuiteCard, SuiteButton, SuiteEyebrow } from "@/components/suite";
import { sortCompaniesIt } from "@/lib/sortCompanies";
import { useEffectiveRole } from "@/foundation/context";
import { fmtHours } from "@/foundation/formatting";

export default function ImportTabulato() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useCompanySelection();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rows, setRows] = useState([]); // editable copy of preview.employees
  const [existingMatricole, setExistingMatricole] = useState({});
  // v0.99.30-c2-close (C2.2 UI): visibilità dei warning strict di preview e confirm
  const [previewWarnings, setPreviewWarnings] = useState([]);
  const [confirmResult, setConfirmResult] = useState(null);

  useEffect(() => {
    if (!["admin", "super_admin"].includes(effectiveRole)) return;
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const sorted = sortCompaniesIt(data);
        setCompanies(sorted);
        if (!companyId && sorted.length) setCompanyId(sorted[0].id);
      } catch (e) { toast.error(apiErr(e)); }
    })();
    /* eslint-disable-next-line */
  }, []);

  const analyze = async () => {
    if (!file || !companyId) { toast.error("Seleziona file e azienda"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/legacy-imports/tabulato/preview", fd, {
        params: { company_id: companyId },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setRows(data.employees.map((e) => ({ ...e, import_this: true })));
      // v0.99.30-c2-close (C2.2): warning strict emessi dal backend
      setPreviewWarnings(data.strict_warnings || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const setRow = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/legacy-imports/tabulato/confirm", {
        company_id: companyId,
        employees: rows,
        overwrite_attendance: true,
        reset_to_zero_at_start: true,
        status: "approved",
      });
      toast.success(
        `Import completato: ${data.employees_created} creati, ${data.employees_updated} aggiornati, ` +
        `${data.attendance_inserted + data.attendance_updated} presenze salvate, ${data.profiles_created} profili orari`
      );
      // v0.99.30-c2-close (C2.2 UI): conserva il risultato per il riepilogo
      // dei warning (matricola sconosciuta, mismatch, profilo mancante).
      setConfirmResult(data);
      setPreview(null); setRows([]); setFile(null);
      setPreviewWarnings([]);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setConfirming(false); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]">
      <div>
        <SuiteEyebrow icon={FileSpreadsheet}>Import dedicato</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800" style={{ fontFamily: "Manrope" }}>
          Import Tabulato Semestrale
        </h1>
        <p className="text-sm text-suite-neutral-500 mt-1">
          Carica il tabulato in uso (Studio Leone Paladin / EASYPRE) per popolare in un colpo solo dipendenti,
          profili orari storicizzati, ratei e storia presenze del semestre.
        </p>
      </div>

      {!preview && (
        <SuiteCard padding="lg" testid="tab-upload-card">
          <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3">
              {["admin", "super_admin"].includes(effectiveRole) && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-suite-neutral-500 block mb-1">Azienda destinazione</Label>
                  <Select value={companyId || ""} onValueChange={setCompanyId}>
                    <SelectTrigger className="w-72" data-testid="tab-company-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div
              className="border-2 border-dashed border-suite-neutral-300 rounded-md p-8 text-center bg-suite-neutral-50 hover:bg-suite-neutral-100 cursor-pointer"
              onClick={() => document.getElementById("tab-input").click()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
              onDragOver={(e) => e.preventDefault()}
              data-testid="tab-dropzone"
            >
              <FileSpreadsheet size={40} className="mx-auto text-suite-primary mb-2" />
              <div className="text-sm font-semibold text-suite-neutral-800">
                {file ? file.name : "Trascina il file 'Tabulato-presenze' qui o clicca per selezionarlo"}
              </div>
              <div className="text-xs text-suite-neutral-500 mt-1">Il file deve contenere un foglio per dipendente (formato Studio Leone Paladin)</div>
              <input id="tab-input" type="file" accept=".xlsx" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0])} data-testid="tab-file-input" />
            </div>
            <div className="flex justify-end">
              <SuiteButton onClick={analyze} disabled={!file || !companyId || loading}
                      icon={Upload} loading={loading} data-testid="tab-analyze-btn">
                {loading ? "Analisi…" : "Analizza tabulato"}
              </SuiteButton>
            </div>
          </div>
        </SuiteCard>
      )}

      {preview && (
        <>
          {/* v0.99.30-c2-close (C2.2 UI): riepilogo warning strict del preview */}
          {previewWarnings.length > 0 && (
            <SuiteCard padding="md" testid="tab-preview-warnings">
              <div className="flex items-start gap-2">
                <div className="text-amber-600 font-semibold text-sm">
                  ⚠ {previewWarnings.length} avvisi (import strict)
                </div>
              </div>
              <div className="mt-2 max-h-56 overflow-y-auto border rounded divide-y divide-suite-neutral-200">
                {previewWarnings.map((w, i) => (
                  <div key={i} className="px-3 py-2 text-xs" data-testid={`tab-warn-${i}`}>
                    <div className="font-mono text-suite-neutral-500 uppercase text-[10px]">{w.code}</div>
                    <div className="text-suite-neutral-800">{w.message}</div>
                    {(w.matricola || w.employee_id || w.last_name) && (
                      <div className="text-[11px] text-suite-neutral-500 mt-0.5">
                        {w.matricola && <span>matricola=<b>{w.matricola}</b> </span>}
                        {w.last_name && <span>· dipendente=<b>{w.last_name}</b></span>}
                        {w.employee_id && <span>· id={w.employee_id.slice(0,8)}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SuiteCard>
          )}

          <SuiteCard padding="md" testid="tab-period-card">
              <div className="text-xs uppercase tracking-widest text-suite-neutral-500 font-semibold">Periodo rilevato</div>
              <div className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>
                {MONTHS_IT[preview.period?.start_month - 1]} {preview.period?.start_year} — {MONTHS_IT[preview.period?.end_month - 1]} {preview.period?.end_year}
              </div>
              <div className="text-sm text-suite-neutral-500 mt-1">
                Trovati <b>{preview.employees.length}</b> dipendenti nel tabulato.
                Attribuisci una matricola a ciascuno (obbligatoria per il matching con il cartellino).
              </div>
          </SuiteCard>

          <SuiteCard padding="none" className="overflow-hidden overflow-x-auto" testid="tab-preview-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Dipendente</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Matricola *</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Aliquota</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Orario dal</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Profili derivati</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Ratei ferie iniz.</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Presenze</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Match esistente</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => {
                    const nPresenze = (r.months || []).reduce((a, m) => a + (m.daily || []).filter((d) => !d.skip).length, 0);
                    return (
                      <TableRow key={idx} data-testid={`tab-row-${idx}`}>
                        <TableCell>
                          <Checkbox checked={r.import_this !== false}
                                    onCheckedChange={(v) => setRow(idx, { import_this: !!v })}
                                    data-testid={`tab-import-${idx}`} />
                        </TableCell>
                        <TableCell>
                          <div className="font-semibold">{r.last_name} {r.first_name}</div>
                          <div className="text-xs text-slate-500">foglio: {r.sheet_name}</div>
                        </TableCell>
                        <TableCell>
                          <Input value={r.matricola || ""}
                                 onChange={(e) => setRow(idx, { matricola: e.target.value })}
                                 className="w-24 font-mono"
                                 placeholder="—"
                                 data-testid={`tab-matr-${idx}`} />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {r.part_time_ratio && r.part_time_ratio < 0.99
                            ? <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">{(r.part_time_ratio * 100).toFixed(1)}% PT</Badge>
                            : <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Full</Badge>}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{formatDateIT(r.orario_valido_dal)}</TableCell>
                        <TableCell className="text-xs">
                          {(r.derived_profiles || []).map((p, pi) => (
                            <div key={pi} className="text-slate-600">
                              <span className="font-semibold">{p.effective_from === "PRE" ? "iniziale" : formatDateIT(p.effective_from)}</span>:
                              {" "}
                              {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"]
                                .map((d) => fmtHours(p.weekly?.[d] || 0)).join("/")}
                            </div>
                          ))}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {Number(r.ratei?.ferie_iniziale_gg || 0).toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{nPresenze}</TableCell>
                        <TableCell>
                          {r.existing_employee_id
                            ? <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200">esistente</Badge>
                            : <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">nuovo</Badge>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
          </SuiteCard>

          <div className="flex justify-end gap-2">
            <SuiteButton variant="secondary" onClick={() => { setPreview(null); setRows([]); }}>Annulla</SuiteButton>
            <SuiteButton onClick={confirm} disabled={confirming}
                    icon={CheckCircle2} loading={confirming} data-testid="tab-confirm-btn">
              {confirming ? "Import in corso…" : `Conferma import (${rows.filter((r) => r.import_this !== false).length} dipendenti)`}
            </SuiteButton>
          </div>
        </>
      )}

      {/* v0.99.30-c2-close (C2.2 UI): riepilogo finale dopo conferma import */}
      {confirmResult && (
        <SuiteCard padding="md" testid="tab-confirm-summary">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-suite-neutral-500 font-semibold">Import concluso</div>
              <div className="text-lg font-bold text-suite-neutral-800 mt-1">
                {confirmResult.attendance_inserted + confirmResult.attendance_updated} presenze salvate ·{" "}
                {(confirmResult.strict_warnings || []).length} avvisi
              </div>
            </div>
            <SuiteButton variant="secondary" onClick={() => setConfirmResult(null)}>Chiudi riepilogo</SuiteButton>
          </div>
          {(confirmResult.strict_warnings || []).length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto border rounded divide-y divide-suite-neutral-200"
                 data-testid="tab-confirm-warnings-list">
              {confirmResult.strict_warnings.map((w, i) => (
                <div key={i} className="px-3 py-2 text-xs">
                  <div className="font-mono text-amber-600 uppercase text-[10px]">{w.code}</div>
                  <div className="text-suite-neutral-800">{w.message}</div>
                  {(w.matricola || w.employee_id || w.last_name) && (
                    <div className="text-[11px] text-suite-neutral-500 mt-0.5">
                      {w.matricola && <span>matricola=<b>{w.matricola}</b> </span>}
                      {w.last_name && <span>· dipendente=<b>{w.last_name}</b></span>}
                      {w.employee_id && <span>· id={w.employee_id.slice(0,8)}</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3">
            <SuiteButton
              variant="secondary"
              size="sm"
              data-testid="tab-download-warnings"
              onClick={() => {
                const blob = new Blob([JSON.stringify(confirmResult, null, 2)], { type: "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `import_report_${Date.now()}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              Scarica riepilogo (JSON)
            </SuiteButton>
          </div>
        </SuiteCard>
      )}
    </div>
  );
}

